// The signed-in user, shared by every page: loaded once from GET /user, refreshed after any change that
// touches it (verification, invitation acceptance, leaving), cleared on sign-out.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import i18n, { rememberLanguage } from './i18n';
import { ApiError, api } from './services/api';
import { hasSession } from './services/tokenStore';
import type { Locale, User } from './types';

export type SessionState = { status: 'loading' } | { status: 'anonymous' } | { status: 'signed-in'; user: User };

export interface Session {
  state: SessionState;
  user: User | null;
  /** Re-read GET /user (after a change the page did not make itself). */
  reload: () => Promise<User | null>;
  /** Adopt a user object a route already received (signup, verify, accept, leave). */
  setUser: (user: User) => void;
  signOut: () => Promise<void>;
  /** The toggle: the UI language now, and the account's `locale` when signed in. */
  setLocale: (locale: Locale) => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

export function SessionProvider({ children, initial }: { children: ReactNode; initial?: SessionState }) {
  const [state, setState] = useState<SessionState>(initial ?? (hasSession() ? { status: 'loading' } : { status: 'anonymous' }));

  const reload = useCallback(async (): Promise<User | null> => {
    if (!hasSession()) {
      setState({ status: 'anonymous' });
      return null;
    }
    try {
      const user = await api.me();
      setState({ status: 'signed-in', user });
      if (user.locale !== i18n.language) void i18n.changeLanguage(user.locale);
      return user;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setState({ status: 'anonymous' });
        return null;
      }
      // The API is unreachable: keep whatever we had rather than bouncing to the login.
      setState((s) => (s.status === 'loading' ? { status: 'anonymous' } : s));
      return null;
    }
  }, []);

  useEffect(() => {
    if (state.status === 'loading') void reload();
  }, [reload, state.status]);

  // A 401 on any later call (the session expired or was revoked, the refresh refused): signed-in pages go to /login.
  useEffect(() => api.onUnauthorized(() => setState({ status: 'anonymous' })), []);

  const setUser = useCallback((user: User) => {
    setState({ status: 'signed-in', user });
    if (user.locale !== i18n.language) void i18n.changeLanguage(user.locale);
  }, []);

  const signOut = useCallback(async () => {
    await api.logout();
    setState({ status: 'anonymous' });
  }, []);

  const setLocale = useCallback(
    async (locale: Locale) => {
      await i18n.changeLanguage(locale);
      rememberLanguage(locale);
      if (state.status === 'signed-in' && state.user.locale !== locale) {
        try {
          const user = await api.patchAccount({ locale });
          setState({ status: 'signed-in', user });
        } catch {
          /* the UI language changed anyway */
        }
      }
    },
    [state],
  );

  const value = useMemo<Session>(
    () => ({ state, user: state.status === 'signed-in' ? state.user : null, reload, setUser, signOut, setLocale }),
    [state, reload, setUser, signOut, setLocale],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession outside SessionProvider');
  return ctx;
}
